import CartUtils from './CartUtils';
import cartDrawer from './cartDrawer';
import checkoutRouting from './checkoutRouting';

/* What is on the cart only because a subscription is: the coupon's free gift
   and the bundled GWP, and anything put on at a promo price — the pre-cart
   offer and the drawer's upsell add-ons both go on as promoItem
   (CartDrawerProvider.jsx:225 and 262).

   The gift bag is deliberately not here. It is an ordinary paid product that
   wraps the order rather than the plan, so it outlives one. */
export const cameWithTheSubscription = (entry) =>
  !!entry &&
  !!(entry.promoItem || entry.isAddedCouponProduct || entry.isBundledGWPProduct);

/* Taking the plan off takes what came with it.

   The server does most of this already: removeSubscriptionFromCart drops the
   coupon's products and then empties the cart outright if only promo items are
   left (bump-backend cart.directives.js:993-1008). Two gaps are why this sweep
   exists anyway. A promo item survives that rule whenever a plain product
   survives with it — a cart holding the plan, a drawer upsell and a standalone
   box keeps the upsell at its promo price with no plan to justify it. And the
   temp-cart branch of deleteSubFromCart does none of it (CartUtils.js:313): an
   anonymous shopper is on that branch until the account step gives them a
   token, and it only nulls the subscription.

   So the plan goes first, and the sweep then runs against whatever the cart
   actually says — nothing on the token path in the common case, the gift and
   the promos on the anonymous one. */
export const removeSubscription = async (cartInfo, readCart) => {
  await CartUtils.deleteSubFromCart(cartInfo, false, null);

  /* No fallback to the pre-removal cart: the anonymous branch of
     deleteProductFromCart splices by index into a temp cart it re-reads from
     storage (CartUtils.js:215-221), so indexes from a cart we could not read
     would land on whatever sits at that position now. A promo item left at its
     promo price is the cheaper failure. */
  const after = await readCart();
  if (!after) return;
  const entries = after.productsAndDetails || [];

  // Last first: a temp-cart removal splices, which re-indexes everything below
  // it, and the index is the only thing addressing entries that have no _id.
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (cameWithTheSubscription(entries[index])) {
      // eslint-disable-next-line no-await-in-loop
      await CartUtils.deleteProductFromCart(
        after,
        entries[index]._id,
        null,
        index,
      );
    }
  }
};

export default { cameWithTheSubscription, removeSubscription };

export const planIdInCart = (cart) => cart?.subscription?.plan?._id || null;

/* Drawer arm only: elsewhere a plan's Add To Cart keeps its old behavior.
   getCart, not the drawer's read, so a guest is not given an account. */
export const readPlanIdInCart = async () => {
  await checkoutRouting.ready();
  if (!cartDrawer.isEnabled()) return null;
  return planIdInCart(await CartUtils.getCart());
};
